/* eslint-disable no-useless-escape */
import AtSymbolIcon from '@heroicons/react/outline/AtSymbolIcon'
import CalendarIcon from '@heroicons/react/outline/CalendarIcon'
import CheckCircleIcon from '@heroicons/react/outline/CheckCircleIcon'
import CheckIcon from '@heroicons/react/outline/CheckIcon'
import DocumentTextIcon from '@heroicons/react/outline/DocumentTextIcon'
import HashtagIcon from '@heroicons/react/outline/HashtagIcon'
import LinkIcon from '@heroicons/react/outline/LinkIcon'
import MenuAlt1Icon from '@heroicons/react/outline/MenuAlt1Icon'
import MenuAlt2Icon from '@heroicons/react/outline/MenuAlt2Icon'
import SelectorIcon from '@heroicons/react/outline/SelectorIcon'

import {
  CustomFieldSchema,
  CustomFieldType,
  CustomFieldValidators,
} from '@tribeplatform/gql-client/types'

export enum CustomFieldSubtype {
  URL = 'url',
  EMAIL = 'email',
  CHECKBOX = 'checkbox',
  SELECT = 'select',
  MULTISELECT = 'multiselect',
  TEXTAREA = 'textarea',
}

export enum CustomFieldValidatorPattern {
  URL = `^https?:\/\/`,
  EMAIL = `^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$`,
  ANGELLIST_PROFILE = `^https?:\/\/(?:www\.)?angel\.co\/(?<type>u|p)\/(?<user>[A-z0-9_%-]+)\/?$`,
  CRUNCHBASE_PROFILE = `^https?:\/\/(?:www\.)?crunchbase\.com\/person\/(?<person>[A-z0-9_%-]+)\/?$`,
  FACEBOOK_PROFILE = `^https?:\/\/(?:www\.)?(?:facebook|fb)\.com\/(?<profile>(?![A-z]+\.php)(?!marketplace|gaming|watch|me|messages|help|search|groups)[-A-z0-9_.%]+)\/?$`,
  GITHUB_PROFILE = `^https?:\/\/(?:www\.)?github\.com\/(?<login>[A-z0-9_%-]+)\/?$`,
  INSTAGRAM_PROFILE = `^https?:\/\/(?:www\.)?(?:instagram\.com|instagr\.am)\/(?<username>[A-Za-z0-9_%](?:(?:[A-Za-z0-9_%]|(?:\.(?!\.))){0,28}(?:[A-Za-z0-9_]))?)\/?$`,
  LINKEDIN_PROFILE = `^https?:\/\/(?:www\.)?linkedin\.com\/(company|school|in)\/(?<permalink>[A-z0-9-.%]+)\/?$`,
  MEDIUM_PROFILE = `^https?:\/\/(?:www\.)?medium\.com\/@(?<username>[A-z0-9%]+)\/?$`,
  REDDIT_PROFILE = `^https?:\/\/(?:[a-z]+\.)?reddit\.com\/(?:u(?:ser)?)\/(?<username>[=A-z0-9_%-]+)\/?$`,
  SNAPCHAT_PROFILE = `^https?:\/\/(?:www\.)?snapchat\.com\/add\/(?<username>[-A-z0-9._%]+)\/?$`,
  STACKOVERFLOW_PROFILE = `^https?:\/\/(?:www\.)?stackoverflow\.com\/users\/(?<id>[0-9]+)\/(?<username>[-A-z0-9_.%]+)\/?$`,
  TELEGRAM_PROFILE = `^https?:\/\/(?:www\.)?(?:t(?:elegram)?\.me|telegram\.org)\/(?<username>[A-z0-9_%]{5,32})\/?$`,
  TWITTER_PROFILE = `^https?:\/\/(?:[A-z]+\.)?twitter\.com\/@?(?!home|share|privacy|tos)(?<username>[A-z0-9_%]+)\/?$`,
  VIMEO_PROFILE = `^https?:\/\/(?:www\.)?vimeo\.com\/user(?<id>[0-9]+)\/?$`,
  YOUTUBE_CHANNEL = `^https?:\/\/(?:[A-z]+\.)?youtube\.com\/`,
}

export const fieldTypes: Partial<CustomFieldSchema>[] = [
  {
    name: 'Text',
    type: CustomFieldType.text,
    description: 'a single line of text',
  },
  {
    name: 'Textarea',
    type: CustomFieldType.text,
    settings: [
      { key: 'subtype', value: JSON.stringify(CustomFieldSubtype.TEXTAREA) },
    ],
    description: 'multiple lines of text',
  },
  {
    name: 'Number',
    type: CustomFieldType.number,
    description: 'a number with formatting options',
  },
  {
    name: 'Select',
    description: 'select from a list of options',
    type: CustomFieldType.text,
    settings: [
      { key: 'subtype', value: JSON.stringify(CustomFieldSubtype.SELECT) },
    ],
    validators: [
      {
        validation: CustomFieldValidators.enum,
        value: JSON.stringify([]),
      },
    ],
  },
  {
    name: 'Multi-select',
    description: 'Tag with values from a list of options',
    type: CustomFieldType.array,
    settings: [
      { key: 'subtype', value: JSON.stringify(CustomFieldSubtype.MULTISELECT) },
    ],
    items: {
      key: 'options',
      name: 'Options',
      type: CustomFieldType.text,
      validators: [
        {
          validation: CustomFieldValidators.enum,
          value: JSON.stringify([]),
        },
      ],
    },
  },
  {
    name: 'Date',
    description: 'a date with picker',
    type: CustomFieldType.date,
  },
  {
    name: 'Toggle',
    type: CustomFieldType.boolean,
    description: 'an on/off switch',
  },
  {
    name: 'Checkbox',
    type: CustomFieldType.boolean,
    settings: [
      { key: 'subtype', value: JSON.stringify(CustomFieldSubtype.CHECKBOX) },
    ],
    description: 'a single checkbox',
  },
  {
    name: 'Rich text',
    description: 'a rich text with formatting options',
    type: CustomFieldType.richText,
  },
  {
    name: 'URL',
    description: 'a link to a page on the web',
    type: CustomFieldType.text,
    settings: [
      { key: 'subtype', value: JSON.stringify(CustomFieldSubtype.URL) },
    ],
    validators: [
      {
        validation: CustomFieldValidators.pattern,
        value: JSON.stringify(CustomFieldValidatorPattern.URL),
        customErrorMessage: 'Links should start with https://',
      },
    ],
  },
  {
    name: 'Email',
    description: 'reference an email address',
    type: CustomFieldType.text,
    settings: [
      { key: 'subtype', value: JSON.stringify(CustomFieldSubtype.EMAIL) },
    ],
    validators: [
      {
        validation: CustomFieldValidators.pattern,
        value: JSON.stringify(CustomFieldValidatorPattern.EMAIL),
        customErrorMessage: 'Please enter a valid email',
      },
    ],
  },
]

export const getFieldSetting = (field, key) => {
  const value = field?.settings?.find(setting => setting.key === key)?.value
  if (!value) return null
  try {
    return JSON.parse(value)
  } catch (err) {
    logger.error('Cannot parse value in custom field')
    return null
  }
}

export const setFieldSetting = (field, key, value) => {
  const setting = field?.settings?.find(setting => setting.key === key)
  value = JSON.stringify(value)
  if (setting) setting.value = value
  else {
    if (!field.settings) field.settings = []
    field.settings.push({ key, value })
  }
  return field
}

export const getFieldTypeName = field => {
  let name
  const subtype = getFieldSetting(field, 'subtype')
  if (subtype) {
    name = fieldTypes.find(type => {
      return getFieldSetting(type, 'subtype') === subtype
    })?.name
  }
  if (!name) {
    name = fieldTypes.find(type => type.type === field.type)?.name
  }
  return name
}
export const getFieldIcon = field => {
  const fieldIcons = {
    [CustomFieldType.date]: <CalendarIcon />,
    [CustomFieldType.number]: <HashtagIcon />,
    [CustomFieldType.boolean]: <CheckCircleIcon />,
    [CustomFieldType.richText]: <DocumentTextIcon />,
    [CustomFieldSubtype.TEXTAREA]: <MenuAlt1Icon />,
    [CustomFieldSubtype.URL]: <LinkIcon />,
    [CustomFieldSubtype.SELECT]: <SelectorIcon />,
    [CustomFieldSubtype.EMAIL]: <AtSymbolIcon />,
    [CustomFieldSubtype.CHECKBOX]: <CheckIcon />,
  }

  const icon = getFieldSetting(field, 'icon')
  if (icon) {
    return (
      <img
        src={`/images/icons/${icon}.svg`}
        alt={field?.name}
        className="w-full h-full"
      />
    )
  }

  const key = field?.subtype || getFieldSetting(field, 'subtype') || field?.type

  return fieldIcons[key] || <MenuAlt2Icon />
}
